import React, { useMemo, useState } from 'react';
import { isEqual } from 'lodash';
import styled from '@emotion/styled';
import { darken, lighten, alpha } from '@mui/material/styles';
import { Box, Button, Stack, Typography } from '@mui/material';

import { useAppSelector } from '@/redux/hooks';
import { FilterLayoutsFilter } from '@/redux/slices/slice_types';
import { selectUserPreferences } from '@/redux/selectors/authSelectors';
import { selectCurrentTableKey } from '@/redux/selectors/dataTableSelectors';
import { useToast } from '@/components/_common/Toast';
import { Modal } from '@/components/_common';
import PolarisIcon from '@/components/_common/PolarisIcon';
import UnsavedChangesDialog from '@/components/_common/Filters/FilterLayoutsModal/UnsavedChangesDialog';
import { FilterType } from '@/components/_common/Filters/FilterDictionary';
import { SUCCESSFUL_SAVE_MESSAGE } from '@/components/_common/Filters/FilterLayoutsModal/constants';
import { useSaveFiltersSettings } from '@/components/_common/Filters/FilterLayoutsModal/useSaveFiltersSettings';
import FilterLayoutColumns from '@/components/_common/Filters/FilterLayoutsModal/FilterLayoutColumns';
import { LayoutModalItems } from '@/components/_common/Filters/FilterLayoutsModal/types';
import { mapFiltersById } from '@/components/_common/Filters/Filters.utils';

interface FilterLayoutsModalProps {
  isOpen: boolean;
  filters: FilterType[];
  defaultFilters: FilterType[];
  quickFilters?: FilterType[];
  defaultQuickFilters?: FilterType[];
  closeModal: () => void;
}

// Filters that we don't want to appear in the Layout Modal
const HIDDEN_FILTER_IDS = ['search'];

const getInitialModalItems = (savedFilters: FilterLayoutsFilter[] = [], filters: FilterType[], quickFilters?: FilterType[]): LayoutModalItems => {
  const result: LayoutModalItems = { show: [], hide: [] };
  const addedFilters = new Set<string>();

  const filtersById = mapFiltersById(filters);
  const quickFiltersById = quickFilters ? mapFiltersById(quickFilters) : undefined;
  savedFilters = structuredClone(savedFilters); // `savedFilters` are retrieved from the redux and we might want to modify them inside this function, that's why we want to clone it first

  savedFilters.forEach((savedFilter) => {
    const { id } = savedFilter;

    // skip the filter if it's neither in `filters` nor in `quickFilters`, which mean layout was saved when filter existed, but now this filter doesn't exist.
    if (!quickFiltersById) {
      if (!(id in filtersById)) {
        return;
      }
    } else if (!(id in filtersById) && !(id in quickFiltersById)) {
      return;
    }

    if (HIDDEN_FILTER_IDS.includes(id)) return;

    if (savedFilter.is_hidden) {
      result.hide.push(savedFilter);
    } else {
      result.show.push(savedFilter);
    }
    addedFilters.add(savedFilter.id);
  });

  // First, we add filters from the `filter` array because we want items in the modal's "Show" column to appear in the same order as in the `filter` array.
  filters.forEach(({ id, label }) => {
    if (!addedFilters.has(id) && !HIDDEN_FILTER_IDS.includes(id)) {
      result.show.push({
        id,
        label,
        is_quick: quickFiltersById ? id in quickFiltersById : false,
        is_hidden: false
      });
      addedFilters.add(id);
    }
  });

  if (quickFilters) {
    quickFilters.forEach(({ id, label }) => {
      if (!addedFilters.has(id)) {
        if (!HIDDEN_FILTER_IDS.includes(id)) {
          result.show.push({
            id,
            label,
            is_quick: true,
            is_hidden: false
          });
          addedFilters.add(id);
        }
      }
    });
  }

  return result;
};

// TODO: add loader for server requests
const FilterLayoutsModal: React.FC<FilterLayoutsModalProps> = ({
  isOpen,
  filters,
  defaultFilters,
  quickFilters,
  defaultQuickFilters,
  closeModal
}) => {
  const { success } = useToast();
  const successSaveToast = () => success(SUCCESSFUL_SAVE_MESSAGE);
  const tableKey = useAppSelector(selectCurrentTableKey);
  const userPrefs = useAppSelector(selectUserPreferences);
  const saveFiltersToPrefs = useSaveFiltersSettings();

  const savedFilters = tableKey ? userPrefs[tableKey]?.filters : undefined;
  const defaultItemsState = useMemo(
    () => getInitialModalItems(undefined, defaultFilters, defaultQuickFilters),
    [defaultFilters, defaultQuickFilters]
  );
  const initialItemsState = useMemo(() => getInitialModalItems(savedFilters, filters, quickFilters), [savedFilters, filters, quickFilters]);

  const [items, setItems] = useState<LayoutModalItems>(initialItemsState);
  const [showUnsavedChanges, setShowUnsavedChanges] = useState<boolean>(false);
  const hasUnsavedChanges = useMemo(() => !isEqual(items, initialItemsState), [items, initialItemsState]);
  const isEqualToDefault = useMemo(() => isEqual(items, defaultItemsState), [items, defaultItemsState]);

  const saveChanges = () => {
    const updatedFilters = [...items.show, ...items.hide];
    saveFiltersToPrefs(updatedFilters)
      .then(() => {
        successSaveToast();
        closeModal();
      })
      .catch((error: unknown) => console.error('Error saving filters:', error));
  };

  const revertChanges = async () => {
    setItems({ show: [...defaultItemsState.show], hide: [...defaultItemsState.hide] });
  };

  const cancelChanges = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedChanges(true);
      return;
    }
    closeModal();
  };

  const discardUnsavedChanges = () => {
    closeModal();
    setItems(initialItemsState);
    setShowUnsavedChanges(false);
  };

  return (
    <>
      <ModalStyled
        open={isOpen}
        title={<ModalTitle>Filter Layout</ModalTitle>}
        icon={<PolarisIcon height={15} icon="filter_lines" />}
        onClose={cancelChanges}
      >
        <Stack direction="row" alignItems="center" gap={2} pt={1} pb={4}>
          <InfoIconContainer>
            <PolarisIcon icon="info_filled" width={20} />
          </InfoIconContainer>
          <InfoText>
            Drag filters left-to-right to show or hide them from the layout. Click-and-drag filters in the list to reorder. Activate the pin-button to
            determine which filters will appear on the main screen.
          </InfoText>
        </Stack>

        <FilterLayoutColumns items={items} setItems={setItems} />

        <Stack direction="row" gap={1.5} mt={4}>
          <ButtonStyled
            variant="outlined"
            color="primary"
            disabled={isEqualToDefault}
            startIcon={<PolarisIcon icon="reset" height={16} />}
            onClick={revertChanges}
          >
            Reset to Defaults
          </ButtonStyled>
          <Box flexGrow={1} />
          <ButtonStyled color="default" onClick={cancelChanges}>
            Cancel
          </ButtonStyled>
          <ButtonStyled variant="contained" disabled={!hasUnsavedChanges} onClick={saveChanges}>
            Save
          </ButtonStyled>
        </Stack>
      </ModalStyled>
      <UnsavedChangesDialog isOpen={showUnsavedChanges} onCancel={() => setShowUnsavedChanges(false)} onConfirm={discardUnsavedChanges} />
    </>
  );
};

export default FilterLayoutsModal;

const ModalStyled = styled(Modal)`
  & > .MuiPaper-root {
    border-radius: ${({ theme }) => `${theme.shape.borderRadius * 2.4}px`};
  }
`;

const ModalTitle = styled(Typography)`
  font-size: 1.357rem;
  line-height: 1.714rem;
  font-weight: 700;
`;

const InfoIconContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  padding: ${({ theme }) => theme.spacing(1)};
  border-radius: ${({ theme }) => theme.shape.borderRadius * 1.2 + 'px'};
  background-color: ${({ theme }) => (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.12)')};
`;

const InfoText = styled(Typography)`
  font-weight: 400;
  font-size: 1.143rem;
  line-height: 1.3;
  color: ${({ theme }) => theme.palette.default.main};
`;

const ButtonStyled = styled(Button)(({ theme, variant, color }) => {
  const colorModifyFn = theme.palette.mode === 'dark' ? lighten : darken;

  return {
    padding: theme.spacing(0.75, 1.5),
    fontWeight: 700,

    ...(variant === 'outlined' && !color
      ? {
          color: theme.palette.default.main,
          borderColor: theme.palette.default.main,

          '&:hover': {
            color: colorModifyFn(theme.palette.default.main, 0.5),
            borderColor: colorModifyFn(theme.palette.default.main, 0.5),
            backgroundColor: alpha(theme.palette.default.main, 0.1)
          }
        }
      : {}),

    ...(variant === undefined ? {} : {})
  };
});
